import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { DataTable } from '../../common/admin/shared/DataTable';
import { IBulletinTableRow, IBulletinListResponse } from '@/types/admin/bulletins';
import { IColumnConfig,IPaginationModel } from '@/types/admin/dataTable';
import { formatBulletinDate, formatBulletinTime, getBulletinPriority } from '@/utils/admin/bulletinUtils';
import { ADMIN_COLORS } from '@/constants/admin';
import { SVG_COMPONENTS } from '@/assets/admin';

interface BulletinDataTableProps {
  data: IBulletinListResponse;
  isLoading: boolean;
  // Table controls optional; DataTable will fallback to context
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  filterModel?: any; // custom filter shape
  onFilterChange?: (filter: any) => void;
  sortModel?: any[];
  onSortChange?: (sort: any[]) => void;
  paginationModel?: IPaginationModel;
  onPaginationChange?: (pagination: IPaginationModel) => void;
  hasNextPage?: boolean;
  hasPreviousPage?: boolean;
  onNextPage?: () => void;
  onPreviousPage?: () => void;
  onBulletinSelect: (bulletin: IBulletinTableRow) => void;
  updateCsvData?: (data: IBulletinTableRow[]) => void;
}

// Define columns for bulletin table
const bulletinColumns: IColumnConfig<IBulletinTableRow>[] = [
  {
    key: 'title',
    label: 'SUBJECT',
    width: '30%',
    sortable: true,
    filterable: true,
    renderCell: (value: any, row: IBulletinTableRow) => (
      <View style={styles.titleCell}>
        <View style={styles.titleRow}>
          <Text style={styles.titleText} numberOfLines={1}>
            {row.title}
          </Text>
          {getBulletinPriority({ 
            highlight: row.highlight, 
            pinHomepage: row.pinHomepage 
          } as any) === 'high' && (
            <View style={styles.priorityIndicator}>
              <SVG_COMPONENTS.CHEVRON_RIGHT width={12} height={12} color={ADMIN_COLORS.WARNING} />
            </View>
          )}
        </View>
        {row.recipientType !== 'all' && (
          <Text style={styles.recipientType}>
            {row.recipientType === 'notice' ? 'Lease Notice' : row.recipientType}
          </Text>
        )}
      </View>
    ),
  },
  {
    key: 'date',
    label: 'SENT DATE',
    width: '15%',
    sortable: true,
    filterable: false,
    renderCell: (value: any, row: IBulletinTableRow) => (
      <Text style={styles.dateText}>
        {formatBulletinDate(row.date)}
      </Text>
    ),
  },
  {
    key: 'time',
    label: 'SENT TIME',
    width: '15%',
    sortable: false,
    filterable: false,
    renderCell: (value: any, row: IBulletinTableRow) => (
      <Text style={styles.timeText}>
        {formatBulletinTime(row.time)}
      </Text>
    ),
  },
  {
    key: 'messageReadCount',
    label: 'READ',
    width: '10%',
    sortable: true,
    filterable: false,
    renderCell: (value: any, row: IBulletinTableRow) => (
      <Text style={styles.numberText}>
        {row.messageReadCount}
      </Text>
    ),
  },
  {
    key: 'pushNotificationSentCount',
    label: 'DELIVERED',
    width: '15%',
    sortable: true,
    filterable: false,
    renderCell: (value: any, row: IBulletinTableRow) => (
      <Text style={styles.numberText}>
        {row.pushNotificationSentCount}
      </Text>
    ),
  },
  {
    key: 'messageSentCount',
    label: 'RECIPIENTS',
    width: '15%',
    sortable: true,
    filterable: false,
    renderCell: (value: any, row: IBulletinTableRow) => (
      <Text style={styles.numberText}>
        {row.messageSentCount}
      </Text>
    ),
  },
];

export const BulletinDataTable: React.FC<BulletinDataTableProps> = ({
  data,
  isLoading,
  searchQuery,
  onSearchChange,
  filterModel,
  onFilterChange,
  sortModel,
  onSortChange,
  paginationModel,
  onPaginationChange,
  hasNextPage,
  hasPreviousPage,
  onNextPage,
  onPreviousPage,
  onBulletinSelect,
  updateCsvData,
}) => {
  return (
    <DataTable<IBulletinTableRow>
      data={{
        rows: data.data.map(bulletin => ({
          id: bulletin.id,
          title: bulletin.title,
          subject: bulletin.subject || bulletin.title,
          date: bulletin.createdAt ? new Date(bulletin.createdAt) : null,
          time: bulletin.createdAt ? new Date(bulletin.createdAt) : null,
          messageSentCount: bulletin.messageSentCount,
          messageReadCount: bulletin.messageReadCount,
          pushNotificationSentCount: bulletin.pushNotificationSentCount,
          highlight: bulletin.highlight,
          pinHomepage: bulletin.pinHomepage,
          publishType: bulletin.publishType,
          recipientType: bulletin.recipientType,
        })),
        count: data.count,
      }}
      columns={bulletinColumns}
      isLoading={isLoading}
      searchQuery={searchQuery}
      onSearchChange={onSearchChange}
      searchPlaceholder="Search bulletins..."
      filterModel={filterModel}
      onFilterChange={onFilterChange}
      sortModel={sortModel}
      onSortChange={onSortChange}
      paginationModel={paginationModel}
      onPaginationChange={onPaginationChange}
      hasNextPage={hasNextPage}
      hasPreviousPage={hasPreviousPage}
      onNextPage={onNextPage}
      onPreviousPage={onPreviousPage}
      onRowSelect={onBulletinSelect}
      updateCsvData={updateCsvData}
      emptyStateType="bulletins"
      mobileColumnCount={2}
    />
  );
};

const styles = StyleSheet.create({
  titleCell: {
    flex: 1,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  titleText: {
    fontSize: 14,
    fontWeight: '500',
    color: ADMIN_COLORS.TEXT_PRIMARY,
    flex: 1,
  },
  priorityIndicator: {
    flexShrink: 0,
  },
  recipientType: {
    fontSize: 12,
    color: ADMIN_COLORS.TEXT_SECONDARY,
    marginTop: 2,
    fontStyle: 'italic',
  },
  dateText: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_PRIMARY,
  },
  timeText: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_PRIMARY,
  },
  numberText: {
    fontSize: 14,
    fontWeight: '500',
    color: ADMIN_COLORS.TEXT_PRIMARY,
    textAlign: 'center',
  },
});
